import type { AuthSessionId, OperatorId, UserId, UserKeyString } from 'balus-common';
import firebase from 'firebase/compat/app';

// Cloud Functions 側の `createCustomToken()` で付与されるカスタムクレームと同じものを定義する
// https://github.com/levii/balus-app/blob/main/functions/src/user/utils/createCustomToken.ts
type Claims = {
  userId: UserId;
  userKey: UserKeyString;
  userType: 'regular' | 'anonymous';
  sessionId: AuthSessionId | null;
  operatorId: OperatorId | null;
};

export class FirebaseAuthenticationClient {
  static async signInWithCustomToken(token: string): Promise<void> {
    await firebase.auth().signInWithCustomToken(token);
  }

  static async signInWithPopupToGoogle(): Promise<void> {
    const provider = new firebase.auth.GoogleAuthProvider();
    await firebase.auth().signInWithPopup(provider);
  }

  static async signInWithRedirectToGoogle(): Promise<void> {
    const provider = new firebase.auth.GoogleAuthProvider();
    await firebase.auth().signInWithRedirect(provider);
  }

  static async signInWithRedirectToMicrosoft(): Promise<void> {
    const provider = new firebase.auth.OAuthProvider('microsoft.com');
    await firebase.auth().signInWithRedirect(provider);
  }

  static async signOut(): Promise<void> {
    await firebase.auth().signOut();
  }

  static async getIdToken(): Promise<string | null> {
    const token = await firebase.auth().currentUser?.getIdToken();
    return token || null;
  }

  static async getIdTokenResult(forceRefresh?: boolean): Promise<firebase.auth.IdTokenResult | null> {
    const result = await firebase.auth().currentUser?.getIdTokenResult(forceRefresh);
    return result || null;
  }

  static async getClaims(): Promise<Claims | null> {
    const token = await this.getIdTokenResult();
    if (!token) return null;

    return token.claims as Claims;
  }

  static onAuthStateChanged(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nextOrObserver: (a: firebase.User | null) => any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: (a: firebase.auth.Error) => any,
    completed?: firebase.Unsubscribe
  ): firebase.Unsubscribe {
    return firebase.auth().onAuthStateChanged(nextOrObserver, error, completed);
  }

  static onIdTokenChanged(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nextOrObserver: (a: firebase.User | null) => any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: (a: firebase.auth.Error) => any,
    completed?: firebase.Unsubscribe
  ): firebase.Unsubscribe {
    return firebase.auth().onIdTokenChanged(nextOrObserver, error, completed);
  }

  /**
   * ログイン中のユーザーに Google アカウントを連携(link)するためのリダイレクトを開始する。
   * 復帰後の結果は {@link getRedirectResult} で取得する。
   */
  static async linkWithRedirectToGoogle(): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('linkWithRedirectToGoogle: not signed in');

    const provider = new firebase.auth.GoogleAuthProvider();
    await currentUser.linkWithRedirect(provider);
  }

  /**
   * ログイン中のユーザーに Microsoft アカウントを連携(link)するためのリダイレクトを開始する。
   * 復帰後の結果は {@link getRedirectResult} で取得する。
   */
  static async linkWithRedirectToMicrosoft(): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('linkWithRedirectToMicrosoft: not signed in');

    const provider = new firebase.auth.OAuthProvider('microsoft.com');
    await currentUser.linkWithRedirect(provider);
  }

  /**
   * リダイレクト認証(signInWithRedirect / linkWithRedirect)からの復帰結果を取得する。
   * リダイレクト復帰でない通常のページロードでは user が null の結果を返す。
   * 連携(link)復帰の判定には result.operationType === 'link' を利用する。
   *
   * Firebase の getRedirectResult() はページロードごとに1回だけ有効な結果を返す(以降は null)。
   * Google/Microsoft の両ハンドラが並行して呼ぶため、Promise をメモ化して同じ結果を共有する。
   */
  private static _redirectResultPromise: Promise<firebase.auth.UserCredential> | null = null;

  static getRedirectResult(): Promise<firebase.auth.UserCredential> {
    if (!this._redirectResultPromise) {
      this._redirectResultPromise = firebase.auth().getRedirectResult();
    }
    return this._redirectResultPromise;
  }

  /**
   * ログイン中のユーザーから Google アカウントの連携を解除(unlink)する。
   * 未連携(auth/no-such-provider)の場合は解除済みとみなして正常終了する(冪等)。
   *
   * NOTE: Admin SDK の providersToUnlink による unlink は既存 ID トークンを失効させ、
   * 直後のクライアント操作が auth/user-token-expired で強制サインアウトになるため、
   * unlink は必ずクライアント SDK で行う(自セッションは維持される)。
   */
  static async unlinkGoogle(): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('unlinkGoogle: not signed in');

    try {
      await currentUser.unlink('google.com');
    } catch (error) {
      // ローカルの providerData が古い場合(別タブ/セッションで解除済み等)は既に未連携であり、
      // 解除としては成功しているため握りつぶす
      if ((error as { code?: string }).code === 'auth/no-such-provider') return;
      throw error;
    }
  }

  /**
   * ログイン中のユーザーから Microsoft アカウントの連携を解除(unlink)する。
   * 未連携(auth/no-such-provider)の場合は解除済みとみなして正常終了する(冪等)。
   *
   * NOTE: Admin SDK の providersToUnlink による unlink は既存 ID トークンを失効させ、
   * 直後のクライアント操作が auth/user-token-expired で強制サインアウトになるため、
   * unlink は必ずクライアント SDK で行う(自セッションは維持される)。
   */
  static async unlinkMicrosoft(): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('unlinkMicrosoft: not signed in');

    try {
      await currentUser.unlink('microsoft.com');
    } catch (error) {
      // ローカルの providerData が古い場合(別タブ/セッションで解除済み等)は既に未連携であり、
      // 解除としては成功しているため握りつぶす
      if ((error as { code?: string }).code === 'auth/no-such-provider') return;
      throw error;
    }
  }

  /**
   * 現在のユーザー情報を Firebase Auth サーバーから再読込し、providerData 等を最新化する。
   */
  static async reloadCurrentUser(): Promise<void> {
    await firebase.auth().currentUser?.reload();
  }

  /**
   * 現在のユーザーにメールアドレス確認メールを送信する。
   * continueUrl を指定すると、確認完了後にそのURLへリダイレクトされる。
   */
  static async sendEmailVerification(continueUrl: string): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('sendEmailVerification: not signed in');
    await currentUser.sendEmailVerification({ url: continueUrl });
  }

  /**
   * 現在のユーザーの ID トークンを強制リフレッシュする。
   * メール確認後にこのメソッドを呼ぶことで AuthStateLoader の onIdTokenChanged がトリガーされ、
   * emailVerified の変化が検知される。
   */
  static async forceRefreshToken(): Promise<void> {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) throw new Error('forceRefreshToken: not signed in');
    await currentUser.getIdToken(true);
  }

  /**
   * 現在のユーザーのメールアドレスが確認済みかどうかを返す。
   * reloadCurrentUser() の後に呼ぶことで最新の状態を取得できる。
   */
  static isEmailVerified(): boolean {
    return firebase.auth().currentUser?.emailVerified ?? false;
  }

  /**
   * 現在のユーザーに連携されている認証プロバイダの一覧を返す。未ログイン時は null。
   * 例: Google 連携済みの判定は providerId === 'google.com' の要素の有無で行う。
   */
  static getProviderData(): (firebase.UserInfo | null)[] | null {
    return firebase.auth().currentUser?.providerData ?? null;
  }
}
